import { useEffect, useRef, useState } from 'react';
import { type Language, setLanguage, useLanguage, t } from '../../i18n';

export function LanguageSwitch({ surface }: { surface: 'login' | 'topbar' | 'mobile' }) {
  const language = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const languages: Array<{ code: Language; nativeLabel: string }> = [
    { code: 'en', nativeLabel: 'English' },
    { code: 'zh-CN', nativeLabel: '中文' },
    { code: 'ja', nativeLabel: '日本語' },
  ];

  const mobile = surface === 'mobile';
  const topbar = surface === 'topbar';
  const triggerClassName = topbar ? 'topbar-ctl-btn icon-only' : `language-switch-${surface}`;

  const handleLanguageChange = (lang: Language) => {
    setIsOpen(false);
    if (lang !== language) {
      void setLanguage(lang);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        containerRef.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('keydown', escape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', escape);
    };
  }, [isOpen]);

  if (mobile) {
    return (
      <div className="mobile-more-item language-switch-mobile">
        <LanguageIcon />
        <span>{t('common.language')}</span>
        <div className="language-switch-dropdown-mobile">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`language-option${language === lang.code ? ' active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
            >
              {lang.nativeLabel}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="language-switch">
      <button
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('common.language')}
        data-ui-tooltip={topbar ? t('common.language') : undefined}
        data-ui-tooltip-placement={topbar ? 'bottom' : undefined}
        onClick={() => setIsOpen(!isOpen)}
      >
        <LanguageIcon />
      </button>

      <div
        className={`language-switch-dropdown${isOpen ? ' open' : ''}`}
        aria-hidden={!isOpen}
        inert={!isOpen}
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`language-option${language === lang.code ? ' active' : ''}`}
            onClick={() => handleLanguageChange(lang.code)}
          >
            {lang.nativeLabel}
          </button>
        ))}
      </div>
    </div>
  );
}

function LanguageIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 5h9M7.5 3v2M4.5 6.5C5.2 9 7 11 9.5 12.3M11.5 6.5C10.4 9.6 8 12 5 13.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 21l3.8-9.5h.4L22 21M15.1 18h5.8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
